import { Suspense, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, Send } from "lucide-react";
import { track, getConsent, setConsent, trackContact, waLink } from "../lib/analytics";
import { STEPS } from "../lib/testimonials";

export function CookieBanner() {
  const [visible, setVisible] = useState(() => !getConsent());
  if (!visible) return null;
  const choose = (v) => {
    setConsent(v);
    setVisible(false);
  };
  return (
    <div className="fixed bottom-4 left-4 z-[90] w-[calc(100vw-32px)] max-w-[440px] rounded-xl border border-[#E3E6EA] bg-white p-6 shadow-[0_18px_50px_rgba(30,40,48,0.16)]" data-testid="cookie-banner" role="dialog" aria-label="Preferenze cookie">
      <p className="text-lg font-bold">La vostra privacy, prima di tutto.</p>
      <p className="mt-2 text-sm leading-relaxed text-[#5B6670]">
        Strumenti essenziali per il sito. Analisi e pubblicità solo con il vostro consenso.{" "}
        <a href="https://www.leonardosoluzioni.it/privacy-policy/" target="_blank" rel="noopener noreferrer" className="underline">
          Dettagli
        </a>
      </p>
      <div className="mt-5 flex items-center justify-end gap-3">
        <button onClick={() => choose("necessary")} data-testid="cookie-necessary" className="min-h-[46px] px-4 text-sm font-semibold text-[#5B6670] transition-colors hover:text-[#1e2830]">
          Solo necessari
        </button>
        <button onClick={() => choose("all")} data-testid="cookie-accept" className="min-h-[46px] rounded-[10px] bg-[#f2dc3c] px-6 text-sm font-bold text-[#1e2830] transition-colors hover:bg-[#e4cb21]">
          Accetta tutti
        </button>
      </div>
    </div>
  );
}

function WhatsAppButton() {
  return (
    <a
      href={waLink()}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackContact("whatsapp")}
      aria-label="Scrivici su WhatsApp"
      data-testid="sf-whatsapp"
      className="sf-wa fixed right-3 z-[85] flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] shadow-[0_6px_18px_rgba(30,40,48,0.25)] transition-transform duration-300 hover:scale-105"
    >
      <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white" aria-hidden="true">
        <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm0 18.03c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.26 8.26 0 01-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 4.54 0 8.24 3.7 8.24 8.24 0 4.55-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28z" />
      </svg>
    </a>
  );
}

export default function SfLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const match = location.pathname.match(/funnel\/?([a-z-]*)/);
  const slug = match ? match[1] : "";
  const idx = STEPS.findIndex((s) => s.slug === slug);
  const step = idx >= 0 ? idx + 1 : 1;
  const current = STEPS[idx >= 0 ? idx : 0];

  useEffect(() => {
    track("funnel_step_view", { step });
  }, [step]);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const go = (n) => {
    if (n === step) return;
    track(n > step ? "funnel_next" : "funnel_back", { step, to: n });
    navigate(n === 1 ? "/funnel" : `/funnel/${STEPS[n - 1].slug}`);
  };

  return (
    <div className="flex min-h-[100svh] flex-col bg-white text-[#1e2830]">
      <a href="#sf-content" className="sr-only">
        Vai al contenuto
      </a>
      <header className="border-b border-[#E3E6EA]">
        <div className="mx-auto flex h-[68px] w-[calc(100%-32px)] items-center justify-between gap-3 md:h-[92px] md:w-[min(1376px,calc(100%-96px))] md:gap-4">
          <button onClick={() => go(1)} className="flex items-center gap-3 text-left md:gap-4" data-testid="sf-header-logo" aria-label="Leonardo Soluzioni — torna all'inizio">
            <img src="/assets/ref-logo.png" alt="Leonardo Soluzioni" className="h-10 w-10 rounded-lg md:h-12 md:w-12" width="48" height="48" />
            <span className="text-[9px] font-semibold leading-[1.5] tracking-[1.2px] text-[#1e2830] md:text-[10.5px]">
              DERATTIZZAZIONE
              <br />
              ELETTRONICA PROFESSIONALE
            </span>
          </button>
          <div className="flex items-center gap-4 md:gap-10">
            <a href="tel:+390536808170" onClick={() => trackContact("telefono_header")} className="flex items-center gap-2 text-[13px] font-bold text-[#1e2830] transition-colors hover:text-[#5B6670] md:gap-2.5 md:text-[15px]" data-testid="sf-header-phone">
              <Phone className="h-4 w-4" strokeWidth={2} /> <span className="whitespace-nowrap">0536 808170</span>
            </a>
            <span className="sf-counter whitespace-nowrap" data-testid="sf-step-counter">
              <strong>{String(step).padStart(2, "0")}</strong> / 07
            </span>
          </div>
        </div>
      </header>

      <main id="sf-content" className="flex-1">
        <div
          key={location.pathname}
          className="sf-arrive mx-auto w-[calc(100%-32px)] md:w-[min(1240px,calc(100%-96px))] pt-5 md:pt-9"
        >
          <Suspense fallback={<div style={{ minHeight: "calc(100svh - 160px)" }} aria-hidden="true" />}>
            <Outlet />
          </Suspense>
        </div>

        <div className="mx-auto mt-9 flex w-[calc(100%-32px)] flex-wrap items-center justify-between gap-3 pb-[calc(env(safe-area-inset-bottom)+200px)] text-[10px] text-[#5B6670] md:mt-12 md:w-[min(1376px,calc(100%-96px))] md:pb-[150px]">
          <span>Leonardo Soluzioni · Sassuolo (MO) · P.IVA 03775840360</span>
          <span className="flex gap-5">
            <a href="https://www.leonardosoluzioni.it/privacy-policy/" target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline">
              Privacy
            </a>
            <a href="https://www.leonardosoluzioni.it/privacy-policy/" target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline">
              Cookie
            </a>
            <button onClick={() => { try { localStorage.removeItem("sf_consent"); } catch (e) {} window.dispatchEvent(new Event("sf-consent-change")); }} className="underline-offset-2 hover:underline" data-testid="sf-cookie-prefs">
              Preferenze cookie
            </button>
          </span>
        </div>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-[80] border-t border-[#E3E6EA] bg-white shadow-[0_-8px_24px_rgba(30,40,48,0.08)]" data-testid="sf-controls">
        <div className="mx-auto flex w-[calc(100%-32px)] flex-col gap-1.5 py-2.5 md:w-[min(1240px,calc(100%-96px))] md:flex-row md:items-center md:gap-6 md:py-3.5">
          <nav className="order-first flex items-center justify-center gap-[5px]" aria-label="Progresso del percorso" data-testid="sf-progress">
            {STEPS.map((s) => (
              <button
                key={s.n}
                onClick={() => go(s.n)}
                title={`${s.n}. ${s.label}`}
                aria-label={`${s.n}. ${s.label}`}
                aria-current={s.n === step ? "step" : undefined}
                data-testid={`sf-dash-${s.n}`}
                className="group flex h-6 items-center"
              >
                <span className={`block h-[4px] w-[17px] rounded-full transition-all duration-300 md:w-6 ${s.n === step ? "bg-[#f2dc3c]" : s.n < step ? "bg-[#9aa3ad] group-hover:bg-[#5B6670]" : "bg-[#dde1e5] group-hover:bg-[#b9c0c7]"}`} />
              </button>
            ))}
          </nav>
          <span className="order-2 hidden text-center text-[11px] font-semibold uppercase tracking-[1.4px] text-[#5B6670] md:block md:flex-1 md:-translate-x-6">
            {current.label}
          </span>
          {step < 7 && (
            <button onClick={() => go(7)} className="order-3 mx-auto text-[11.5px] font-semibold text-[#5B6670] underline underline-offset-4 transition-colors hover:text-[#1e2830]" data-testid="sf-skip-quote">
              Salta e richiedi il preventivo gratuito →
            </button>
          )}
          <div className="order-4 flex items-center justify-between gap-3 md:justify-end">
            {step === 7 ? (
              <>
                <button
                  onClick={() => window.dispatchEvent(new Event("sf-submit-request"))}
                  className="sf-next min-w-0 flex-1 justify-center gap-2 md:min-w-[235px] md:flex-none"
                  data-testid="sf-submit-bar"
                >
                  <Send className="h-4 w-4" /> Invia richiesta
                </button>
                <a href="tel:+390536808170" onClick={() => trackContact("telefono_barra")} aria-label="Chiama 0536 808170" data-testid="sf-call-bar" className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-[10px] border border-[#E3E6EA] bg-white text-[#1e2830] transition-colors hover:border-[#1e2830]">
                  <Phone className="h-5 w-5" />
                </a>
              </>
            ) : (
              <>
                {step > 1 ? (
                  <button onClick={() => go(step - 1)} className="sf-back" data-testid="sf-back">
                    <ArrowLeft className="h-4 w-4" /> Indietro
                  </button>
                ) : (
                  <span />
                )}
                <button onClick={() => go(step + 1)} className="sf-next" data-testid="sf-next">
                  {STEPS[step].cta || STEPS[step].label}
                  <ArrowRight className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <WhatsAppButton />
      <CookieBanner />
    </div>
  );
}

export function useGo() {
  const navigate = useNavigate();
  const go = (n) => {
    track(n > 1 ? "funnel_next" : "funnel_back", { to: n });
    navigate(n === 1 ? "/funnel" : `/funnel/${STEPS[n - 1].slug}`);
  };
  return go;
}
